import { NotificationFilter } from "./types";
import { isNotificationUnread } from "../NotificationCard/content";

import type { NotificationFilterOption } from "./types";

export const NOTIFICATION_FILTER_STORAGE_KEY = "notification_filter";

// Mirrors RECENT_NOTIFICATIONS_CAP_PER_BUCKET in
// application/notification/services/service.py: the backend returns at most
// this many unread and this many read notifications.
const NOTIFICATIONS_CAP_PER_BUCKET = 100;

export const NOTIFICATION_FILTER_OPTIONS: Record<
  NotificationFilter,
  NotificationFilterOption
> = {
  [NotificationFilter.ALL]: {
    label: "All",
    emptyMessage: "You do not currently have any notifications.",
  },
  [NotificationFilter.UNREAD]: {
    label: "Unread",
    emptyMessage: "You have no unread notifications.",
  },
  [NotificationFilter.READ]: {
    label: "Read",
    emptyMessage: "You have no read notifications.",
  },
};

function isNotificationFilter(
  value: string | null,
): value is NotificationFilter {
  return (
    value !== null && Object.values<string>(NotificationFilter).includes(value)
  );
}

export function filterNotifications(props: {
  notifications: Notification[];
  filter: NotificationFilter;
}): Notification[] {
  const { notifications, filter } = props;
  switch (filter) {
    case NotificationFilter.UNREAD:
      return notifications.filter(isNotificationUnread);
    case NotificationFilter.READ:
      return notifications.filter(
        (notification) => !isNotificationUnread(notification),
      );
    default:
      return notifications;
  }
}

// sessionStorage can throw in private windows or with blocked site data, so
// the modal falls back to All instead of failing.
export function getStoredNotificationFilter(): NotificationFilter {
  try {
    const stored = window.sessionStorage.getItem(
      NOTIFICATION_FILTER_STORAGE_KEY,
    );
    return isNotificationFilter(stored) ? stored : NotificationFilter.ALL;
  } catch {
    return NotificationFilter.ALL;
  }
}

export function storeNotificationFilter(props: {
  filter: NotificationFilter;
}): void {
  try {
    window.sessionStorage.setItem(
      NOTIFICATION_FILTER_STORAGE_KEY,
      props.filter,
    );
  } catch {
    // Storage is a convenience only; the filter still works for this session.
  }
}

// The backend only returns notifications the app can render, and its totals
// cover every one of them, not just the capped lists.
export function countNotificationsByFilter(props: {
  totalUnreadCount: number;
  totalReadCount: number;
}): Record<NotificationFilter, number> {
  const { totalUnreadCount, totalReadCount } = props;
  return {
    [NotificationFilter.ALL]: totalUnreadCount + totalReadCount,
    [NotificationFilter.UNREAD]: totalUnreadCount,
    [NotificationFilter.READ]: totalReadCount,
  };
}

// Says which buckets the lists cut off. A bucket is cut off when its tab count
// exceeds the per-bucket cap. Returns null when nothing is cut off.
export function getTruncationMessage(props: {
  filter: NotificationFilter;
  counts: Record<NotificationFilter, number>;
}): string | null {
  const { filter, counts } = props;
  const isUnreadTruncated =
    counts[NotificationFilter.UNREAD] > NOTIFICATIONS_CAP_PER_BUCKET;
  const isReadTruncated =
    counts[NotificationFilter.READ] > NOTIFICATIONS_CAP_PER_BUCKET;
  const recentUnread = `${NOTIFICATIONS_CAP_PER_BUCKET} most recent unread`;
  const recentRead = `${NOTIFICATIONS_CAP_PER_BUCKET} most recent read`;
  const truncatedBuckets = [
    isUnreadTruncated && filter !== NotificationFilter.READ && recentUnread,
    isReadTruncated && filter !== NotificationFilter.UNREAD && recentRead,
  ].filter(Boolean);
  if (truncatedBuckets.length === 0) {
    return null;
  }
  if (filter === NotificationFilter.ALL && truncatedBuckets.length === 1) {
    const completeBucketFilter = isUnreadTruncated
      ? NotificationFilter.READ
      : NotificationFilter.UNREAD;
    if (counts[completeBucketFilter] > 0) {
      const completeBucketName = isUnreadTruncated ? "read" : "unread";
      return `Showing all ${completeBucketName} notifications and the ${truncatedBuckets[0]} ones.`;
    }
  }
  return `Showing the ${truncatedBuckets.join(" and the ")} notifications.`;
}
